import React, { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Grid2 } from '@mui/material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Custom UI wrappers
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomList from '@ui-components/List/CustomList';
import CustomListItem from '@ui-components/List/CustomListItem';
import CustomListItemIcon from '@ui-components/List/CustomListItemIcon';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import SearchIcon from '@assets/images/search.svg';
import CustomButton from '@ui-components/Button/CustomButton';

import '../intakeform/intake.css';
import CustomListItemText from '@ui-components/List/CustomListItemText';
import DragIcon from '@assets/images/drag-icon.svg';
import type { FacilityMapping } from '../../API/services/IntakeFormService';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomDialog from '@ui-components/Dialog/CustomDialog';
import { useUpdateIntakeFormMapping } from '../../API/hooks/IntakeFormHook';
import { hasRole } from '../login/storageUtility';
import InfoIcon from '@assets/images/infoIcon.svg';
import CustomToolTip from '@ui-components/ToolTip/CustomToolTip';

interface TransferListProps {
  assignedFacilities: FacilityMapping[];
  allAvailableFacilities: FacilityMapping[];
  intakeFormName: string;
  intakeFormId: number;
  openDialog: boolean;
  onOpenDialog: (state: boolean) => void;
}

const DroppableContainer = ({
  id,
  children,
}: {
  id: 'left' | 'right';
  children: React.ReactNode;
}) => {
  const { setNodeRef } = useDroppable({ id });

  return <Box ref={setNodeRef}>{children}</Box>;
};

interface SortableItemProps {
  id: number | string;
  item: FacilityMapping;
  handleToggle: (value: number) => () => void;
  checked: number[];
  isAdmin: boolean;
}

interface CustomListRendererProps {
  items: FacilityMapping[];
  sortableItems: number[];
  handleToggle: (value: number) => () => void;
  checked: number[];
  isAdmin: boolean;
}

const CustomListRenderer: React.FC<CustomListRendererProps> = ({
  items,
  sortableItems,
  handleToggle,
  checked,
  isAdmin,
}) => (
  <CustomList dense component="ul">
    <SortableContext
      items={sortableItems}
      strategy={verticalListSortingStrategy}
    >
      {items.map((item) => (
        <SortableItem
          key={item.id}
          id={item.id}
          item={item}
          handleToggle={handleToggle}
          checked={checked}
          isAdmin={isAdmin}
        />
      ))}
    </SortableContext>
  </CustomList>
);

function not(a: FacilityMapping[], b: FacilityMapping[]) {
  return a.filter((value) => !b.includes(value));
}

function notNumberArray(a: number[], b: number[]) {
  return a.filter((value) => !b.includes(value));
}
function intersection(a: number[], b: number[]) {
  return a.filter((value) => b.includes(value));
}

// Sortable item
const SortableItem: React.FC<SortableItemProps> = ({
  id,
  item,
  handleToggle,
  checked,
  isAdmin,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isAdmin });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isAdmin ? 'grab' : 'default',
    background: checked.includes(item.id) ? '#f1f3f4' : 'transparent',
    opacity: isDragging ? 0.7 : 1,
  };

  const labelId = `transfer-list-item-${item.id}-label`;

  return (
    <CustomListItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isAdmin ? listeners : {})}
      onClick={isAdmin ? handleToggle(item.id) : undefined}
      className="intake-form-list-item"
    >
      <CustomListItemIcon>
        <Checkbox
          id={`transfer-list-item-${item.id}`}
          checked={checked.includes(item.id)}
          tabIndex={-1}
          disableRipple
          color="secondary"
          disabled={!isAdmin}
        />
      </CustomListItemIcon>
      <DragIcon />
      <CustomListItemText id={labelId} className="pl-12" primary={item.name} />
      {item.intakeFormName !== null && (
        <Box className="mr-8 item-align-center">
          <CustomToolTip
            id="tooltip-pid"
            title={item.intakeFormName ?? ''}
            className="cursor-pointer"
          >
            <InfoIcon />
          </CustomToolTip>
        </Box>
      )}
    </CustomListItem>
  );
};

const onSuccessfullUpdateIntakeFormMapping = () => {
  console.log('succesfully mapped intake form');
};

const TransferList: React.FC<TransferListProps> = ({
  assignedFacilities,
  allAvailableFacilities,
  intakeFormName,
  intakeFormId,
  openDialog,
  onOpenDialog,
}) => {
  const isAdmin = hasRole(['ops-admin']);
  const { mutate: updateIntakeFormMapping } = useUpdateIntakeFormMapping(
    onSuccessfullUpdateIntakeFormMapping
  );

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [checked, setChecked] = useState<number[]>([]);
  const [items, setItems] = useState({
    left: assignedFacilities,
    right: allAvailableFacilities,
  });

  const [assignedSearchQuery, setAssignedSearchQuery] = useState('');
  const [availableSearchQuery, setAvailableSearchQuery] = useState('');

  useEffect(() => {
    if (openDialog) {
      setErrorMessage(null);
      setChecked([]);
      setItems({
        left: assignedFacilities,
        right: allAvailableFacilities,
      });
      setAssignedSearchQuery('');
      setAvailableSearchQuery('');
    }
  }, [openDialog, assignedFacilities, allAvailableFacilities]);

  const left = items.left;
  const right = items.right;

  const leftChecked = intersection(
    checked,
    left.map((facility) => facility.id)
  );
  const rightChecked = intersection(
    checked,
    right.map((facility) => facility.id)
  );

  const handleToggle = (value: number) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];
    if (currentIndex === -1) newChecked.push(value);
    else newChecked.splice(currentIndex, 1);
    setChecked(newChecked);
  };

  const handleCheckedRight = () => {
    setItems({
      left: not(
        left,
        leftChecked.map((id) => left.find((f) => f.id === id)!)
      ),
      right: right.concat(
        leftChecked.map((id) => left.find((f) => f.id === id)!)
      ),
    });
    setChecked(notNumberArray(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setItems({
      left: left.concat(
        rightChecked.map((id) => right.find((f) => f.id === id)!)
      ),
      right: not(
        right,
        rightChecked.map((id) => right.find((f) => f.id === id)!)
      ),
    });
    setChecked(notNumberArray(checked, rightChecked));
  };

  // Sensors for drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
      disabled: !isAdmin,
    })
  );

  type Container = 'left' | 'right';

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!active || !over) return;

    const activeId = active.id as number;
    const overId = over.id as number | Container;

    const activeContainer: Container = left.some(
      (facility) => facility.id === activeId
    )
      ? 'left'
      : 'right';
    let overContainer: Container;

    if (overId === 'left' || overId === 'right') {
      overContainer = overId;
    } else if (left.some((facility) => facility.id === overId)) {
      overContainer = 'left';
    } else {
      overContainer = 'right';
    }

    const selectedItems = checked.includes(activeId)
      ? intersection(
          checked,
          items[activeContainer].map((facility) => facility.id)
        )
      : [activeId];

    // Case 1: Reorder within same list
    if (activeContainer === overContainer && typeof overId === 'number') {
      const current = items[activeContainer];
      const remaining = current.filter(
        (facility) => !selectedItems.includes(facility.id)
      );
      const overIndex = remaining.findIndex(
        (facility) => facility.id === overId
      );
      const insertAt = overIndex === -1 ? remaining.length : overIndex;

      const newArray = [
        ...remaining.slice(0, insertAt),
        ...selectedItems.map(
          (id) => current.find((facility) => facility.id === id)!
        ),
        ...remaining.slice(insertAt),
      ];

      setItems((prev) => ({
        ...prev,
        [activeContainer]: newArray,
      }));
    }

    // Case 2: Move between lists
    else if (activeContainer !== overContainer) {
      const sourceList = items[activeContainer];
      const destList = items[overContainer];

      const newSource = sourceList.filter(
        (facility) => !selectedItems.includes(facility.id)
      );
      const newDest = [
        ...destList,
        ...selectedItems.map(
          (id) => sourceList.find((facility) => facility.id === id)!
        ),
      ];

      setItems((prev) => ({
        ...prev,
        [activeContainer]: newSource,
        [overContainer]: newDest,
      }));
    }

    // Uncheck moved items
    setChecked((prev) => notNumberArray(prev, selectedItems));
  };

  const handleSave = async () => {
    if (leftChecked.length || rightChecked.length) {
      setErrorMessage('Please move the selected items before saving.');
      return;
    }
    const leftIds = left.map((facility) => facility.id);
    try {
      updateIntakeFormMapping({
        userId: '1',
        intakeFormId: intakeFormId,
        assignedFacilities: leftIds,
      });
      onOpenDialog(false);
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const handleAssignedSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAssignedSearchQuery(event.target.value);
  };

  const handleAvailableSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAvailableSearchQuery(event.target.value);
  };

  const filteredLeftItems = useMemo(() => {
    return left.filter((item) =>
      item.name.toLowerCase().includes(assignedSearchQuery.toLowerCase())
    );
  }, [left, assignedSearchQuery]);

  const filteredRightItems = useMemo(() => {
    return right.filter((item) =>
      item.name.toLowerCase().includes(availableSearchQuery.toLowerCase())
    );
  }, [right, availableSearchQuery]);
  return (
    <Box>
      <CustomDialog
        id="intake-form-facility-mapping-dialog"
        open={openDialog}
        onClose={() => {
          onOpenDialog(true);
        }}
        disableEscapeKeyDown={true}
        maxWidth="md"
        fullWidth
      >
        <CustomDialogContent className="intake-dialog-content">
          <Box className="mb-32">
            <CustomTypography
              id="intake-form-facility-mapping-title"
              variant="h5"
              color="textPrimary"
            >
              Assign Facilities
            </CustomTypography>
            <CustomTypography
              id="intake-form-facility-mapping-title"
              variant="body3"
              color="textDisabled"
              className="pt-8"
            >
              {intakeFormName}
            </CustomTypography>
          </Box>
          <Box className="pb-80">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Box className="flex justify-center">
                <Grid2
                  container
                  justifyContent="center"
                  spacing={2}
                  className="width-100"
                >
                  <Grid2 size={{ xs: 5 }}>
                    <Box className="pb-20 flex">
                      <Grid2 container className="width-100">
                        <Grid2 size={8}>
                          <CustomTypography
                            id="transfer-list-title"
                            variant="body2"
                            color="textDisabled"
                          >
                            Assigned Facilities
                          </CustomTypography>
                        </Grid2>
                        <Grid2 size={4} className="text-align-right">
                          <CustomTypography
                            id="transfer-list-count"
                            variant="body2"
                            color="textDisabled"
                          >
                            {left.length} Facilities
                          </CustomTypography>
                        </Grid2>
                      </Grid2>
                    </Box>
                    <Box className="pb-16">
                      <CustomTextField
                        id={`transfer-list-search-assigned-facilities`}
                        type="text"
                        placeholder="Search Assigned Facilities"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={assignedSearchQuery}
                        onChange={handleAssignedSearchChange}
                        disabled={!isAdmin}
                        startAdornment={
                          <CustomIconButton
                            id="search-icon"
                            size="small"
                            icon={<SearchIcon />}
                          />
                        }
                      />
                    </Box>
                    <DroppableContainer id="left">
                      {filteredLeftItems.length > 0 ? (
                        <CustomListRenderer
                          items={filteredLeftItems}
                          sortableItems={left.map((f) => f.id)}
                          handleToggle={handleToggle}
                          checked={checked}
                          isAdmin={isAdmin}
                        />
                      ) : (
                        <CustomTypography
                          id="no-facilities-assigned"
                          variant="body3"
                          color="textDisabled"
                        >
                          No Facilities Found
                        </CustomTypography>
                      )}
                    </DroppableContainer>
                  </Grid2>
                  <Grid2
                    size={{ xs: 2 }}
                    className="flex justify-center align-items-center text-align-center mt-32"
                  >
                    <Box>
                      <Box className="mb-12">
                        <CustomButton
                          id="right-arrow-button"
                          variant="outlined"
                          onClick={handleCheckedRight}
                          disabled={!isAdmin || leftChecked.length === 0}
                          className="wider-arrow"
                          color="secondary"
                        >
                          &gt;
                        </CustomButton>
                      </Box>
                      <Box className="mb-12">
                        <CustomButton
                          id="left-arrow-button"
                          variant="outlined"
                          onClick={handleCheckedLeft}
                          disabled={!isAdmin || rightChecked.length === 0}
                          className="wider-arrow"
                          color="secondary"
                        >
                          &lt;
                        </CustomButton>
                      </Box>
                      <Box>
                        <CustomTypography
                          id="drag-and-drop-text"
                          variant="body3"
                          color="primary"
                        >
                          Drag & drop or <br /> select and click
                          <br /> arrows
                        </CustomTypography>
                      </Box>
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 5 }}>
                    <Box className="pb-20 flex">
                      <Grid2 container className="width-100">
                        <Grid2 size={8}>
                          <CustomTypography
                            id="transfer-list-title"
                            variant="body2"
                            color="textDisabled"
                          >
                            All Facilities
                          </CustomTypography>
                        </Grid2>
                        <Grid2 size={4} className="text-align-right">
                          <CustomTypography
                            id="transfer-list-count"
                            variant="body2"
                            color="textDisabled"
                          >
                            {right.length} Facilities
                          </CustomTypography>
                        </Grid2>
                      </Grid2>
                    </Box>
                    <Box className="pb-16">
                      <CustomTextField
                        id={`transfer-list-search-available-facilities`}
                        type="text"
                        placeholder="Search All Facilities"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={availableSearchQuery}
                        onChange={handleAvailableSearchChange}
                        disabled={!isAdmin}
                        startAdornment={
                          <CustomIconButton
                            id="search-icon"
                            size="small"
                            icon={<SearchIcon />}
                          />
                        }
                      />
                    </Box>
                    <DroppableContainer id="right">
                      {filteredRightItems.length > 0 ? (
                        <CustomListRenderer
                          items={filteredRightItems}
                          sortableItems={right.map((f) => f.id)}
                          handleToggle={handleToggle}
                          checked={checked}
                          isAdmin={isAdmin}
                        />
                      ) : (
                        <CustomTypography
                          id="no-facilities-assigned"
                          variant="body3"
                          color="textDisabled"
                        >
                          No Facilities Found
                        </CustomTypography>
                      )}
                    </DroppableContainer>
                  </Grid2>
                </Grid2>
              </Box>
            </DndContext>
          </Box>
        </CustomDialogContent>

        <Box className="intake-dialog-footer">
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <Box className="display-flex justify-end">
                {hasRole(['ops-admin']) ? (
                  <>
                    <Box className="pr-12 align-items-center">
                      {errorMessage && (
                        <CustomTypography
                          id="error-message"
                          color="error"
                          variant="body2"
                        >
                          {errorMessage}
                        </CustomTypography>
                      )}
                    </Box>

                    <Box className="pr-12">
                      <CustomButton
                        id="cancel-intake-form-mapping-button"
                        onClick={() => onOpenDialog(false)}
                        variant="outlined"
                      >
                        Cancel
                      </CustomButton>
                    </Box>

                    <Box>
                      <CustomButton
                        id="save-intake-form-mapping-button"
                        onClick={handleSave}
                        variant="contained"
                      >
                        Save
                      </CustomButton>
                    </Box>
                  </>
                ) : (
                  <Box className="pr-12">
                    <CustomButton
                      id="close-intake-form-mapping-button"
                      onClick={() => onOpenDialog(false)}
                      variant="outlined"
                    >
                      Close
                    </CustomButton>
                  </Box>
                )}
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      </CustomDialog>
    </Box>
  );
};

export default TransferList;
